import { Controller, type ActionEvent } from "@hotwired/stimulus"
import type { StripeClient, StripeElement, StripeElements } from "../types/global"

const STRIPE_SRC = "https://js.stripe.com/v3/"

// Matches the app's .form-control inputs: the system font stack in Tailwind's
// gray-700, gray-400 placeholders, red-600 when invalid. Stripe renders each
// field in its own iframe, so these are literal colors, not CSS variables.
const ELEMENT_STYLE = {
  base: {
    color: "#364153",
    fontFamily: "system-ui, -apple-system, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
    fontSize: "16px",
    lineHeight: "24px",
    "::placeholder": { color: "#99a1af" }
  },
  invalid: {
    color: "#e7000b"
  }
}

const ELEMENT_CLASSES = { focus: "focused", empty: "empty", invalid: "invalid" }

let stripeLoader: Promise<void> | undefined

function loadStripeJs(): Promise<void> {
  if (window.Stripe) return Promise.resolve()

  stripeLoader ??= new Promise((resolve, reject) => {
    const script = document.createElement("script")
    script.src = STRIPE_SRC
    script.onload = (): void => resolve()
    script.onerror = (): void => {
      stripeLoader = undefined
      script.remove()
      reject(new Error("Stripe.js failed to load"))
    }
    document.head.appendChild(script)
  })
  return stripeLoader
}

// Connects to data-controller="payment-form"
//
// Replaces the inline jQuery <script> blocks in payments/new.html.erb and
// team_registrations/new_payment.html.erb. Mounts Stripe Elements for the card
// fields, and on submit exchanges them for a Stripe token, then lets the form
// submit normally (through Turbo) with the token in the field named by
// tokenParamValue (payment[token] by default). The old handlers submitted with the native
// form.submit(), which bypassed Turbo, and wrote Stripe's client-side errors
// into a #flash-messages element that no layout renders.
export default class extends Controller {
  static targets = ["form", "cardNumber", "cardExpiry", "cardCvc", "postalCode",
                    "firstName", "lastName", "submit", "error"]
  static values = { publishableKey: String, tokenParam: { type: String, default: "payment[token]" } }

  declare readonly formTarget: HTMLFormElement
  declare readonly cardNumberTarget: HTMLElement
  declare readonly cardExpiryTarget: HTMLElement
  declare readonly cardCvcTarget: HTMLElement
  declare readonly postalCodeTarget: HTMLElement
  declare readonly firstNameTarget: HTMLInputElement
  declare readonly lastNameTarget: HTMLInputElement
  declare readonly submitTarget: HTMLInputElement
  declare readonly errorTarget: HTMLElement
  declare publishableKeyValue: string
  declare tokenParamValue: string

  private stripe?: StripeClient
  private cardNumber?: StripeElement
  private mounted = new Map<string, StripeElement>()
  private tokenReady = false

  async connect(): Promise<void> {
    try {
      await loadStripeJs()
    } catch {
      this.showError("The payment form could not be loaded. Please refresh the page and try again.")
      this.submitTarget.disabled = true
      return
    }
    if (!this.element.isConnected || !window.Stripe) return

    this.stripe = window.Stripe(this.publishableKeyValue)
    const elements = this.stripe.elements()

    this.cardNumber = this.mount("cardNumber", elements, this.cardNumberTarget)
    this.mount("cardExpiry", elements, this.cardExpiryTarget)
    this.mount("cardCvc", elements, this.cardCvcTarget)
    this.mount("postalCode", elements, this.postalCodeTarget)
  }

  disconnect(): void {
    this.mounted.forEach((element) => element.destroy())
    this.mounted.clear()
    this.cardNumber = undefined
    this.stripe = undefined
  }

  // Each Stripe field renders inside its own iframe, which a <label for> cannot
  // reach, so the field's visible label focuses it on click instead.
  focusField({ params }: ActionEvent): void {
    this.mounted.get(params.field as string)?.focus()
  }

  async submit(event: SubmitEvent): Promise<void> {
    // Second pass: the token is attached, so let Turbo submit the form.
    if (this.tokenReady) {
      this.tokenReady = false
      return
    }

    event.preventDefault()
    if (!this.stripe || !this.cardNumber) return

    this.hideError()
    this.submitTarget.disabled = true

    const name = `${this.firstNameTarget.value} ${this.lastNameTarget.value}`.trim()
    const result = await this.stripe.createToken(this.cardNumber, { name })

    this.submitTarget.disabled = false
    if (result.error || !result.token) {
      this.showError(result.error?.message ?? "Your card could not be processed. Please try again.")
      return
    }

    this.setToken(result.token.id)
    this.tokenReady = true
    this.formTarget.requestSubmit()
  }

  private elementOptions(): object {
    return { style: ELEMENT_STYLE, classes: ELEMENT_CLASSES }
  }

  private mount(type: string, elements: StripeElements, target: HTMLElement): StripeElement {
    const element = elements.create(type, this.elementOptions())
    // A Turbo cache snapshot can carry the previous visit's dead iframes.
    target.replaceChildren()
    element.mount(target)
    this.mounted.set(type, element)
    return element
  }

  private setToken(token: string): void {
    this.formTarget.querySelector(`input[name="${CSS.escape(this.tokenParamValue)}"]`)?.remove()

    const input = document.createElement("input")
    input.type = "hidden"
    input.name = this.tokenParamValue
    input.value = token
    this.formTarget.append(input)
  }

  private showError(message: string): void {
    this.errorTarget.textContent = message
    this.errorTarget.hidden = false
  }

  private hideError(): void {
    this.errorTarget.textContent = ""
    this.errorTarget.hidden = true
  }
}
